import React from "react";
import { observer } from "mobx-react";
import moment from "moment";
import cns from "classnames";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  withStyles
} from "@material-ui/core";

import { Classes, ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { Workshift } from "@graphql";

import ReactDatePicker from "@components/DatePicker/ReactDatePicker";
import { CustomerEditorStore } from "../CustomerEditorStore";
import { defaultSpans } from "../utils";

export type WorkshiftSpanTableProps = {
  store: CustomerEditorStore;
  classes: Classes<typeof styles>;
};

@observer
class CustomerWorkshiftSpans extends React.Component<WorkshiftSpanTableProps> {
  renderHeader = () => {
    const { classes } = this.props;

    return (
      <TableHead>
        <TableRow className={classes.header}>
          <TableCell padding="checkbox" />
          <TableCell>{locale.Workshift}</TableCell>
          <TableCell>{locale.From}</TableCell>
          <TableCell>{locale.To}</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  renderSubheader = (profession: string) => {
    return (
      <TableRow>
        <TableCell colSpan={4} align="center" style={{ fontWeight: "bold" }}>
          {profession}
        </TableCell>
      </TableRow>
    );
  };

  renderEmpty = () => {
    return (
      <TableRow>
        <TableCell colSpan={5} align="center">
          Please, select professions
        </TableCell>
      </TableRow>
    );
  };

  renderRow = (profession: string, workshift: Workshift) => {
    const { classes, store } = this.props;

    const data = store.values.WorkshiftSpans.find(
      (x) => x.Profession === profession && x.Workshift === workshift
    );

    const fromMinutes = data ? data.FromMinutes : defaultSpans[workshift].FromMinutes;
    const toMinutes = data ? data.ToMinutes : defaultSpans[workshift].ToMinutes;

    const from = moment().startOf("day").add(fromMinutes, "minutes");

    const to = moment().startOf("day").add(toMinutes, "minutes");

    const datePickerProps = {
      dateFormat: "HH:mm",
      timeFormat: "HH:mm",
      showTimeSelect: true,
      showTimeSelectOnly: true,
      timeIntervals: 15
    };

    return (
      <TableRow
        key={`${profession}.${workshift}`}
        className={cns({
          [classes.disabledRow]: !data
        })}
        data-test-id={`${profession}.${workshift}`}
      >
        <TableCell>
          <Checkbox
            checked={Boolean(data)}
            onChange={(e) =>
              store.selectWorkshiftSpan(profession, workshift, e.target.checked)
            }
          />
        </TableCell>
        <TableCell style={{ width: "100%" }}>{locale.workshifts[workshift]}</TableCell>
        <TableCell>
          <div style={{ width: 100 }}>
            <ReactDatePicker
              {...datePickerProps}
              clearable={false}
              inputFieldProps={{
                disableUnderline: true
              }}
              name="FromMinutes"
              onChange={(m) => {
                const minutes = m ? m.minutes() + m.hours() * 60 : null;
                store.setWorkshiftSpanValue(
                  "FromMinutes",
                  profession,
                  workshift,
                  minutes
                );
              }}
              disabled={!data}
              value={from}
            />
          </div>
        </TableCell>
        <TableCell>
          <div style={{ width: 100 }}>
            <ReactDatePicker
              {...datePickerProps}
              clearable={false}
              inputFieldProps={{
                disableUnderline: true
              }}
              name="ToMinutes"
              onChange={(m) => {
                const minutes = m ? m.minutes() + m.hours() * 60 : null;
                store.setWorkshiftSpanValue("ToMinutes", profession, workshift, minutes);
              }}
              disabled={!data}
              value={to}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  render() {
    const { classes, store } = this.props;

    const professions = store.values.Professions.slice().sortBy((x) => x);

    return (
      <Table className={classes.root} padding="none" data-test="CustomerWorkshiftSpans">
        {this.renderHeader()}

        <TableBody>
          {professions.length === 0 && this.renderEmpty()}

          {professions.map((prof) => {
            return (
              <React.Fragment key={prof}>
                {this.renderSubheader(prof)}

                {this.renderRow(prof, Workshift.Day)}
                {this.renderRow(prof, Workshift.Evening)}
                {this.renderRow(prof, Workshift.Night)}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  }
}

const styles = (theme: ThemeType) => ({
  root: {
    "& td:not(:first-child)": {
      paddingLeft: 4
    }
  },

  header: {
    backgroundColor: theme.palette.secondary.background,

    "& > td": {
      color: theme.palette.secondary.contrast
    }
  },

  disabledRow: {
    "& td": {
      color: theme.palette.grey["500"]
    }
  }
});

export default withStyles(styles)(CustomerWorkshiftSpans);
